// Global fetch interceptor to add Bearer token to all API requests
// Uses in-memory token (works in iframes where localStorage is blocked)
// Falls back to localStorage for page refreshes
const TOKEN_KEY = 'cb_session_token';

let _inMemoryToken = null;

// Set token (called from AuthContext after login/register)
export const setSessionToken = (token) => {
  console.log(`[Auth] Token set: ${token ? token.substring(0, 15) + '...' : 'null'}`);
  _inMemoryToken = token;
  try { localStorage.setItem(TOKEN_KEY, token); } catch (e) { /* iframe */ }
};

// Get token (prefers in-memory, falls back to localStorage)
export const getSessionToken = () => {
  if (_inMemoryToken) return _inMemoryToken;
  try { return localStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
};

// Clear token (called from AuthContext on logout)
export const clearSessionToken = () => {
  _inMemoryToken = null;
  try { localStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ }
};

// Reliable API fetch that explicitly adds Authorization header.
// Use this instead of raw fetch() for all /api/ calls.
// This does NOT depend on the window.fetch monkey-patch below.
const _realFetch = window.fetch.bind(window);

// Tab-disabled toast deduplication — when a member opens a page whose API
// it can't access, we get one 403 per call. Without throttling the user
// would see five identical "X is hidden" toasts. Cap to one per tab per 10s.
const _recentTabToasts = new Map();
const _TAB_TOAST_TTL_MS = 10_000;

const _handleTabDisabledResponse = async (response) => {
  if (response.status !== 403) return response;
  // Clone so the caller can still read the body.
  let payload;
  try {
    payload = await response.clone().json();
  } catch {
    return response;
  }
  const detail = payload?.detail;
  if (!detail || typeof detail !== 'object' || detail.error !== 'tab_disabled') {
    return response;
  }
  const tabId = detail.tab_id || 'unknown';
  const now = Date.now();
  const last = _recentTabToasts.get(tabId);
  if (last && now - last < _TAB_TOAST_TTL_MS) return response;
  _recentTabToasts.set(tabId, now);
  // Dispatch a custom event so any listener (modal, side panel, etc.) can
  // react to a tab-locked event. The default consumer is the global
  // <TabLockedToast/> mounted in App.jsx, which renders the toast + the
  // "Request access" CTA.
  try {
    window.dispatchEvent(new CustomEvent('cb:tab-locked', {
      detail: { tab_id: tabId, message: detail.message },
    }));
  } catch {
    /* SSR safety */
  }
  return response;
};

export const apiFetch = async (url, options = {}) => {
  const token = getSessionToken();
  if (token) {
    options = {
      ...options,
      headers: {
        ...(options.headers || {}),
        'Authorization': `Bearer ${token}`,
      },
    };
  }
  const response = await _realFetch(url, options);
  return _handleTabDisabledResponse(response);
};

// ─── Owner Admin fetch helper ─────────────────────────────────────────────
// Ergonomic wrapper for Owner Admin API calls. Always sends the admin
// session cookie, sets Content-Type on JSON bodies, and (optionally) parses
// the response for you. New Owner Admin components should call this instead
// of raw `fetch(url, { credentials: 'include' })` — but even if they don't,
// the global window.fetch monkey-patch above auto-injects credentials on
// any URL matching `/api/owner-admin/*`.
//
// Usage:
//   const data = await adminFetch('/api/owner-admin/users/search?q=eli').json();
//   await adminFetch(`/api/owner-admin/users/${id}/reset-password`, { method: 'POST' });
//   await adminFetch('/api/owner-admin/tickets', { method: 'POST', body: { subject } });
export const adminFetch = (url, options = {}) => {
  const opts = { credentials: 'include', ...options };
  if (opts.body && typeof opts.body !== 'string' && !(opts.body instanceof FormData)) {
    opts.headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
    opts.body = JSON.stringify(opts.body);
  }
  // Return a Promise that also exposes .json() / .text() shortcuts so callers
  // can chain: `await adminFetch(url).json()`.
  const pending = _realFetch(url, opts).then(_handleTabDisabledResponse);
  pending.json = async () => {
    const res = await pending;
    if (!res.ok) {
      let detail = res.statusText;
      try { detail = (await res.json()).detail || detail; } catch { /* not JSON */ }
      const err = new Error(detail);
      err.status = res.status;
      throw err;
    }
    return res.json();
  };
  pending.text = async () => {
    const res = await pending;
    return res.text();
  };
  return pending;
};

// Global window.fetch monkey-patch (safety net, may not work in all iframe environments)
const originalFetch = window.fetch;

window.fetch = async function(url, options = {}) {
  const token = getSessionToken();

  const isApiUrl = typeof url === 'string' && url.includes('/api/');
  // Owner Admin endpoints are cookie-authenticated (cb_admin_session). Every
  // fetch to those routes MUST send credentials, otherwise the backend
  // returns 401 "Admin authentication required". Developers historically
  // kept forgetting `credentials: 'include'` on new admin components, which
  // caused a recurring dashboard-outage class of bugs. This interceptor
  // makes it impossible to miss: any request to `/api/owner-admin/*` is
  // silently upgraded to include credentials if the caller didn't set them.
  const isOwnerAdminUrl = typeof url === 'string' && url.includes('/api/owner-admin');
  if (isOwnerAdminUrl && !options.credentials) {
    options = { ...options, credentials: 'include' };
  }

  if (isApiUrl) {
    if (token) {
      options = {
        ...options,
        headers: {
          ...(options.headers || {}),
          'Authorization': `Bearer ${token}`,
        },
      };
    }
  }
  
  const response = await originalFetch.call(this, url, options);
  // Only run the tab-disabled toast for /api/ URLs to avoid noise from
  // third-party requests passing through window.fetch.
  if (isApiUrl) {
    return _handleTabDisabledResponse(response);
  }
  return response;
};
